import FreeBreakfastOutlined from "@mui/icons-material/FreeBreakfastOutlined";
import MoreTimeOutlined from "@mui/icons-material/MoreTimeOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useJobs } from "../../../hooks/useJobs";
import type { TranslationKeys } from "../../../i18n/types";
import { BREAK_RULES_COLUMNS, HOURS_RATE_COLUMNS, OVERTIME_COLUMNS, visibleColumns, type WageRuleColumn } from "../../team/WageSettings/wageRuleColumns";
import { ConfigRowsPreview, type ConfigRowsPreviewColumn } from "../../UIToolkit/Tables/ConfigTable/ConfigRowsPreview";
import type { RuleSubject } from "./ruleCardSubjects";
import { type RuleRowsSource, type RuleSectionKind, type RuleSectionModel, ruleSections, sectionKindOf } from "./ruleSections";
import { useRuleCellFormat } from "./useRuleCellFormat";

/** Each section titled and marked as its Wage Settings table is, so the card reads as a piece of that page. */
const SECTION: Readonly<Record<RuleSectionKind, { titleKey: TranslationKeys; icon: ReactNode; columns: readonly WageRuleColumn<string>[] }>> = {
	hours_rate: { titleKey: "department.config.rates.section.hoursRate", icon: <ScheduleOutlined />, columns: HOURS_RATE_COLUMNS },
	overtime: { titleKey: "department.config.rates.overtime.section", icon: <MoreTimeOutlined />, columns: OVERTIME_COLUMNS },
	break: { titleKey: "department.config.rates.breakRules.section", icon: <FreeBreakfastOutlined />, columns: BREAK_RULES_COLUMNS },
};

type Props = {
	subjects: readonly RuleSubject[];
	/** Omitted while today's rows load: each section draws a placeholder row. */
	source?: RuleRowsSource;
	/** Notes about where every section's rows come from (another department, the capture), after its own. */
	notes: readonly string[];
	/**
	 * The rules are the employee's home department's, not the one on screen. Its sections carry a `-home`
	 * test id, and its Job column follows its own rows: only this department's job setup is loaded.
	 */
	isHomeDepartment: boolean;
};

/**
 * A card's rule sections — one compact table per Wage Settings table the subjects come from, with the
 * same columns and headers, the Job column only where the department has jobs (as on Wage Settings). For
 * a home department, whose job setup is not loaded here, where a row it shows is limited to jobs.
 */
export const RuleSectionsView: FC<Props> = ({ subjects, source, notes, isHomeDepartment }) => {
	const { t } = useTranslation();
	const format = useRuleCellFormat();
	const departmentHasJobs = useJobs().length > 0;
	const models: RuleSectionModel[] =
		source === undefined
			? [...new Set(subjects.map(sectionKindOf))].map((kind) => ({ id: kind, kind, rows: [], messages: [], isLegacy: false, mentionsJobs: false }))
			: ruleSections(subjects, source, format);

	return models.map((model) => {
		const { titleKey, icon, columns } = SECTION[model.kind];
		const showJobColumn = isHomeDepartment ? model.mentionsJobs : departmentHasJobs;
		const previewColumns: ConfigRowsPreviewColumn[] = visibleColumns(columns, showJobColumn).map((column) => ({
			id: column.id,
			header: t(column.headerKey),
		}));
		return (
			<ConfigRowsPreview
				key={model.id}
				title={t(titleKey)}
				icon={icon}
				columns={previewColumns}
				rows={model.rows}
				loadingLabel={source === undefined ? t("wageBreakdown.card.loading") : undefined}
				messages={model.isLegacy ? model.messages : [...model.messages, ...notes]}
				dataTestId={`wage-breakdown-card-${model.id}${isHomeDepartment ? "-home" : ""}`}
			/>
		);
	});
};
